import React, { useState } from 'react';
import {
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	TableContainer,
	Button,
	Input,
	Text,
	Flex,
	Textarea,
	Icon,
	Tooltip,
	Menu,
	MenuButton,
	MenuItem,
	MenuGroup,
	MenuList,
	IconButton,
	useToast,
	useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon, ViewIcon } from '@chakra-ui/icons';
import { FaTrashAlt } from 'react-icons/fa';
import { BsFillCheckCircleFill } from 'react-icons/bs';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { CgCloseO } from 'react-icons/cg';
import { RemoteConfigValue } from '@repo/alictus-common/types';
import { ViewFilter } from '../view_filter';
import { GlobalStore } from '../../store';
import { RemoteConfigTypeToString, formatTimestamp } from '../../utility';
import { FilterToString } from '../utils/filter_utils';
import { toggleRCValueStatus } from '../utils/remote_config_utils';
import { PlayerSegment } from '@repo/alictus-common/types/segment';
import config from '../../config';
import axios from 'axios';
import { detectConfigOverlap } from '../utils/overlap';

interface RemoteConfigTableProps {
	globalData: GlobalStore;
	viewFilter: ViewFilter;
	editRCVariableDisclosure: any;
	deleteRCKeyDisclosure: any;
	setDeleteRCKeyData: (data: any) => void;
	multiFunction: (disc: { onOpen: () => void }, remoteConfigValue: RemoteConfigValue) => void;
	segmentationData: PlayerSegment[];
	environment: string;
	statusToggleDisclosure: any; 
    setStatusToggleData: (data: any) => void;
}

export function RemoteConfigTable({
	globalData,
	viewFilter,
	editRCVariableDisclosure,
	deleteRCKeyDisclosure,
	setDeleteRCKeyData,
	multiFunction,
	segmentationData,
	environment,
	statusToggleDisclosure,
	setStatusToggleData,
}: RemoteConfigTableProps) {
	const [sortField, setSortField] = useState<'key' | 'lastUpdated' | 'type' | 'isTest' | 'isPersonal' | 'isActive'>('key');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const [searchQuery, setSearchQuery] = useState(''); // State for search query
	const [isSearchActive, setIsSearchActive] = useState(false);
	const isAdminOrProduct = globalData.user?.roles.includes('ADMIN') || globalData.user?.roles.includes('PRODUCT') || false;
	const toast = useToast();

	const handleSort = (field: 'key' | 'lastUpdated' | 'type' | 'isTest' | 'isPersonal' | 'isActive') => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	async function sendDuplicateRequest(data: RemoteConfigValue | undefined) {
		console.log('Inside sendDuplicateRequest');
		if (!data) return;

		// data has no value, so we need to fetch it

		await downloadRCValue(data.id.toString()).then((res) => {
			if (res) {
				data.value = res.value;
			} else {
				toast({
					title: 'Error',
					description: 'An error occurred while fetching the Remote Config Value.',
					status: 'error',
					duration: 5000,
					isClosable: true,
					position: 'top',
				});
			}
		});

		// Count all the Remote Config Values with the "same key + Copy" pattern
		const count = globalData.remoteConfigValues.filter((item) => item.key.startsWith(data.key + ' Copy')).length;
		data.key = data.key + ' Copy' + (count > 0 ? ` ${count}` : '');

		const request = axios.post(`${config.API_ENDPOINT}/game/${globalData.gameId}/rc_package/1/rc_value`, data, {
			headers: { Authorization: `Bearer ${globalData.JWTToken}` },
		});

		request
			.then((res) => {
				console.log('Success:', res);
				toast({
					title: 'Remote Config Value Duplicated',
					description: 'The Remote Config Value has been duplicated successfully with the key: ' + data.key,
					status: 'success',
					duration: 5000,
					isClosable: true,
					position: 'top',
				});
			})
			.catch((err) => {
				console.error('Error:', err);
				toast({
					title: 'Error',
					description: err.response?.data || 'An error occurred while duplicating the Remote Config Value.',
					status: 'error',
					duration: 5000,
					isClosable: true,
					position: 'top',
				});
			});
	}

	async function downloadRCValue(rc_id: string): Promise<RemoteConfigValue | null> {
		console.log('Download RC Value');
		try {
			const response = await axios.get(`${config.API_ENDPOINT}/game/${globalData.gameId}/rc_package/1/rc_value/${rc_id}`, {
				headers: { Authorization: `Bearer ${globalData.JWTToken}` },
			});
			return response.data as RemoteConfigValue;
		} catch (error) {
			console.error('Error fetching full remote config:', error);
			if (axios.isAxiosError(error)) {
				if (error.response) {
					console.error('Response data:', error.response.data);
					console.error('Response status:', error.response.status);
				} else if (error.request) {
					console.error('No response received:', error.request);
				} else {
					console.error('Error setting up request:', error.message);
				}
			} else {
				console.error('Unexpected error:', error);
			}
			return null;
		}
	}

	async function downloadRCValueText(data: RemoteConfigValue | undefined) {
		console.log('Download RC Text');

		if (!data) return;

		const rcValue = await downloadRCValue(String(data.id));
		console.log(rcValue);
		if (!rcValue) {
			toast({
				title: 'Error',
				description: 'An error occurred while fetching the Remote Config Value.',
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'top',
			});
			return;
		}

		let parsedValue = {};
		let isValueValid = false;
		try {
			parsedValue = JSON.parse(rcValue.value);

			toast({
				title: 'Remote Config Value Downloaded',
				description: 'The Remote Config Value has been downloaded successfully.',
				status: 'success',
				duration: 5000,
				isClosable: true,
				position: 'top',
			});
			isValueValid = true;
		} catch (error) {
			// If the value is not a valid JSON, show an error toast
			// and save the value as is

			toast({
				title: 'Error',
				description: 'The Remote Config Value is not a valid JSON. Downloading as is.',
				status: 'warning',
				duration: 5000,
				isClosable: true,
				position: 'top',
			});

			parsedValue = rcValue.value;
		}
		const jsonContent = JSON.stringify(parsedValue, null, 2);
		const blob = new Blob([jsonContent], { type: 'application/json' });
		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = isValueValid ? `${rcValue.key}.json` : `${rcValue.key}.txt`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	const filteredData = (globalData.remoteConfigValues || []).filter((item) => item.key.toLowerCase().includes(searchQuery.toLowerCase()));

	const sortedData = filteredData.sort((a, b) => {
		let result = 0;
		if (sortField === 'key') {
			result = a.key.localeCompare(b.key);
		} else if (sortField === 'lastUpdated') {
			result = new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
		} else if (sortField === 'type') {
			result = RemoteConfigTypeToString(a.type).localeCompare(RemoteConfigTypeToString(b.type));
		} else if (sortField === 'isTest' || sortField === 'isPersonal' || sortField === 'isActive') {
			result = (a[sortField] ? 1 : 0) - (b[sortField] ? 1 : 0);
		}
		return sortDirection === 'asc' ? result : -result;
	});

	return (
		<TableContainer width="100%">
			<Table variant="simple" size="md" border="0px" width="100%" style={{ tableLayout: 'fixed' }}>
				<Thead sx={{ th: { textAlign: 'center', verticalAlign: 'middle' } }}>
					<Tr>
						<Th width="10%" cursor="pointer">
							<Flex align="center" gap="15px" height="20px">
								<Tooltip label="Search by Name" placement="top">
									<Icon
										as={SearchIcon}
										boxSize={4}
										cursor="pointer"
										onClick={(e) => {
											e.stopPropagation();
											setIsSearchActive(!isSearchActive);
										}}
									/>
								</Tooltip>
								{isSearchActive && (
									<Input
										size="sm"
										placeholder="Search..."
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										autoFocus
										width="100%"
									/>
								)}
								{!isSearchActive && (
									<span onClick={() => handleSort('key')}>Name {sortField === 'key' && (sortDirection === 'asc' ? '↑' : '↓')}</span>
								)}
							</Flex>
						</Th>
						<Th width="10%">Description</Th>
						<Th width="6%" onClick={() => handleSort('type')} cursor="pointer">
							Type {sortField === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
						</Th>
						<Th width="15%">Filter</Th>
						<Th width="10%" onClick={() => handleSort('lastUpdated')} cursor="pointer">
							Latest Update {sortField === 'lastUpdated' && (sortDirection === 'asc' ? '↑' : '↓')}
						</Th>
						<Th width="9%" onClick={() => handleSort('isTest')} cursor="pointer">
							Production {sortField === 'isTest' && (sortDirection === 'asc' ? '↑' : '↓')}
						</Th>
						<Th width="6%" onClick={() => handleSort('isPersonal')} cursor="pointer">
							Public {sortField === 'isPersonal' && (sortDirection === 'asc' ? '↑' : '↓')}
						</Th>
						<Th width="6%" onClick={() => handleSort('isActive')} cursor="pointer">
							Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
						</Th>
						<Th width="6%">View</Th>
						<Th width="6%">Delete</Th>
						<Th width="6%">Action</Th>
					</Tr>
				</Thead>
				<Tbody>
					{sortedData.map(
						(field, i) =>
							viewFilter.canDisplay(field) && (
								<TableRow
									key={i}
									field={field}
									globalData={globalData}
									editRCVariableDisclosure={editRCVariableDisclosure}
									deleteRCKeyDisclosure={deleteRCKeyDisclosure}
									setDeleteRCKeyData={setDeleteRCKeyData}
									multiFunction={multiFunction}
									segmentationData={segmentationData}
									duplicateFunction={sendDuplicateRequest}
									downloadFunction={downloadRCValueText}
									isAdminOrProduct={isAdminOrProduct}
									environment={environment}
									statusToggleDisclosure={statusToggleDisclosure}
									setStatusToggleData={setStatusToggleData}
								/>
							),
					)}
				</Tbody>
			</Table>
		</TableContainer>
	);
}

interface TableRowProps {
	field: RemoteConfigValue;
	globalData: GlobalStore;
	editRCVariableDisclosure: any;
	deleteRCKeyDisclosure: any;
	setDeleteRCKeyData: (data: any) => void;
	multiFunction: (disc: { onOpen: () => void }, remoteConfigValue: RemoteConfigValue) => void;
	segmentationData: PlayerSegment[];
	duplicateFunction: (data: RemoteConfigValue) => void;
	downloadFunction: (data: RemoteConfigValue) => void;
	isAdminOrProduct: boolean;
	environment: string;
	statusToggleDisclosure: any;
	setStatusToggleData: (data: any) => void;
}

function TableRow({
	field,
	globalData,
	editRCVariableDisclosure,
	deleteRCKeyDisclosure,
	setDeleteRCKeyData,
	multiFunction,
	segmentationData,
	duplicateFunction,
	downloadFunction,
	isAdminOrProduct,
	environment,
	statusToggleDisclosure,
    setStatusToggleData,
}: TableRowProps)  {
	const toast = useToast();
	
	const buttonViewColor = useColorModeValue('gray.500', 'gray.500');
	const buttonDeleteColor = useColorModeValue('gray.600', 'gray.200');
	const buttonIconHoverColor = useColorModeValue('gray.800', 'gray.300');

	// Add color values for checkmarks
	const greenColor = useColorModeValue('green.500', 'green.200');
	const redColor = useColorModeValue('red.500', 'red.200');
	const hoverColor = useColorModeValue('gray.600', 'gray.300');

	function showCheckMark(isActive: boolean) {
		return (
			<Flex justify="center" align="center" width="100%" height="100%">
				<Icon as={isActive ? BsFillCheckCircleFill : CgCloseO} color={isActive ? greenColor : redColor}/>
			</Flex>
		);
	}
	const checkOverlapThenToogle = async (globalData: GlobalStore, rcValueId: number) => {
		const currentRcValue = globalData.remoteConfigValues.find((val) => val.id === rcValueId);
		if (!currentRcValue) {
			console.error('Error toggling RC value status: RC Value not found');
			return;
		}

		const existingOneExcludedOverlapCheckList = globalData.remoteConfigValues.filter(
			(val) =>
				val.key === currentRcValue!.key &&
				val.uid !== currentRcValue!.uid &&
				val.isTest === currentRcValue!.isTest &&
				val.isPersonal === currentRcValue!.isPersonal &&
				val.isActive === true,
		);

		const { newConfigSegmentNames, existingConfigSegmentNames, isOverlap } = detectConfigOverlap(
			currentRcValue,
			existingOneExcludedOverlapCheckList,
			segmentationData,
		);

		// If current config is active this means it will be deactivated, so dont need to open up toast
		if (isOverlap && !currentRcValue.isActive) {
			toast({
				title: 'Validation Error',
				description:
					`You can't change status of this RC because it overlaps with existing RC: ${currentRcValue.key}.\n` +
					`New Config Segments: ${newConfigSegmentNames}\n` +
					`Existing Config Segments: ${existingConfigSegmentNames}`,
				status: 'error',
				duration: 7000,
				isClosable: true,
				position: 'top',
			});

			return;
		}

		toggleRCValueStatus(globalData, rcValueId);
	};
	return (
		<Tr>
			<Td
				textAlign="left"
				verticalAlign="middle"
				width="10%"
				overflow="hidden"
				textOverflow="ellipsis"
				whiteSpace="nowrap"
				_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
			>
				{field.key}
			</Td>
			<Td
				textAlign="center"
				verticalAlign="middle"
				width="10%"
				overflow="hidden"
				textOverflow="ellipsis"
				whiteSpace="nowrap"
				position="relative"
				_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
			>
				{field.description}
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="6%">
				{RemoteConfigTypeToString(field.type)}
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="15%">
				{field.filterOperations.length > 0 && (
					<Textarea fontSize={'sm'} border={'0px'} value={FilterToString(field.filterOperations, segmentationData)} isReadOnly />
				)}
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="10%">
				<Tooltip label={`Last updated by: ${field.lastUpdater}`} placement="top">
					<span>{formatTimestamp(field.lastUpdated)}</span>
				</Tooltip>
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="9%">
				{showCheckMark(!field.isTest)}
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="6%">
				{showCheckMark(!field.isPersonal)}
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="6%">
				<Button
					isDisabled={!isAdminOrProduct && environment === 'production'}
					onClick={() => {
                    setStatusToggleData({
                        ...field,
                        onConfirm: async () => {
                            await checkOverlapThenToogle(globalData, field.id);
                            statusToggleDisclosure.onClose();
                        },
                    });
                    statusToggleDisclosure.onOpen();
                }}
					leftIcon={
						<Icon
							as={field.isActive ? BsFillCheckCircleFill : CgCloseO}
							color={field.isActive ? greenColor : redColor}
							_hover={{ color: hoverColor }}
						/>
					}
					variant="simple"
				/>
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="6%">
				<ViewButton
					onOpen={() => multiFunction(editRCVariableDisclosure, field)}
					iconColor={buttonViewColor}
					iconHoverColor={buttonIconHoverColor}
				/>
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="6%">
				<Button
					isDisabled={!isAdminOrProduct}
					onClick={() => {
						setDeleteRCKeyData(field);
						deleteRCKeyDisclosure.onOpen();
					}}
					leftIcon={<Icon as={FaTrashAlt} color={buttonDeleteColor} _hover={{ color: buttonIconHoverColor }} />}
					variant="tableAction"
				/>
			</Td>
			<Td textAlign="center" verticalAlign="middle" width="6%">
				<Menu>
					<MenuButton as={Button} rounded={'full'} variant={'link'} cursor={'pointer'} minW={0}>
						<IconButton 
							aria-label="Add" 
							icon={<PiDotsThreeOutlineVerticalFill color={'gray.500'} />} 				
							backgroundColor={'transparent'}
						   _hover={{ bg: 'transparent', color: 'white' }}/>
					</MenuButton>
					<MenuList>
						<MenuGroup title="Experimental Actions" fontSize={'m'} fontWeight={'bold'} color={'orange.500'}>
							<MenuItem isDisabled={!isAdminOrProduct && environment === 'production'} onClick={() => duplicateFunction(field)}>
								Duplicate RC Value
							</MenuItem>

							<MenuItem onClick={() => downloadFunction(field)}>Download RC Text</MenuItem>
							<MenuItem>Remote Config ID: {field.id}</MenuItem>
						</MenuGroup>
					</MenuList>
				</Menu>
			</Td>
		</Tr>
	);
}


function ViewButton({ onOpen, iconColor, iconHoverColor }: { onOpen: () => void; iconColor: string; iconHoverColor: string }) {
	return (
		<Button
			onClick={onOpen}
			aria-label="View"
			leftIcon={<ViewIcon color={iconColor} _hover={{ color: iconHoverColor }} />}
			variant="tableAction"
		/>
	);
}
